import { SVG } from '@svgdotjs/svg.js';
import {
  geoJson2SvgDrawLabels,
  geoJson2SvgDrawPaths,
} from '../utilities/geojson2svg';
import { GeoJson2SvgSetupWorkerInput } from '../workers/geojson2SvgSetup.worker';
import { useGeoJson2SvgDrawWorker } from './useGeojson2SvgDrawWorker';
import { useGeoJson2SvgSetupWorker } from './useGeojson2SvgSetupWorker';
import { useCallback, useMemo, useState } from 'react';

export function useGeoJson2Svg() {
  const [setupLoading, setSetupLoading] = useState(false);
  const [drawLoading, setDrawLoading] = useState(false);
  const geoJson2SvgSetupWorker = useGeoJson2SvgSetupWorker(setSetupLoading);
  const geoJson2SvgDrawWorker = useGeoJson2SvgDrawWorker(setDrawLoading);

  const generate = useCallback(
    async (...args: GeoJson2SvgSetupWorkerInput) => {
      const setup = await geoJson2SvgSetupWorker(args);

      const svg = SVG();
      svg.width(setup.viewport.width);
      svg.height(setup.viewport.height);

      await geoJson2SvgDrawPaths(svg, setup);
      await geoJson2SvgDrawLabels(svg, setup);

      // credit: https://stackoverflow.com/a/79196371
      const img = new Image();
      img.src = URL.createObjectURL(
        new Blob([svg.svg()], { type: 'image/svg+xml' })
      );
      await img.decode();
      URL.revokeObjectURL(img.src);
      const bmp = await createImageBitmap(img);

      return await geoJson2SvgDrawWorker([bmp, setup]);
    },
    [geoJson2SvgDrawWorker, geoJson2SvgSetupWorker]
  );

  const loading = useMemo(
    () => setupLoading || drawLoading,
    [drawLoading, setupLoading]
  );

  return useMemo(
    () => ({
      generate,
      loading,
    }),
    [generate, loading]
  );
}
